import { useMemo } from "react";
import type { DocumentVisibility, Project } from "@/engine/types";
import { planVisibilityOf, showInPlanList, usePlanVisibilityTick } from "@/features/access/plan-visibility";
import { isOwnExecutionTask, type SelfRef } from "@/features/access/own-assignment";
import { usePlanActor, useSelfRef } from "@/features/access/use-plan-access";
import { useWorkspaceRole } from "@/features/access/workspace-access";
import { useIndexedProjects } from "@/features/files/use-file-entries";
import { useCatalogStore } from "@/store/catalog-store";
import { useProjectStore } from "@/store/project-store";
import { setDocumentVisibility, trashDocument } from "./document-ops";
import { collectWorkspaceDocuments, type WorkspaceDocRow } from "./doc-index";
import { DOCUMENT_HOST } from "./doc-model";
import { canReadDocument, canReadDocumentContent, type DocReader } from "./doc-visibility";

export type ListedWorkspaceDoc = WorkspaceDocRow & {
  reader: DocReader;
  contentReadable: boolean;
};

/** 목록에 보이는 플랜의 문서. 편집 중인 플랜은 저장 전 글이 보이도록 편집기를 쓴다. */
export function useWorkspaceDocRows(): ListedWorkspaceDoc[] {
  const projects = useIndexedProjects();
  const role = useWorkspaceRole();
  const tick = usePlanVisibilityTick();
  const actor = usePlanActor();
  const activeId = useProjectStore((s) => s.activeId);
  const self = useSelfRef();
  const displayName = useCatalogStore((s) => s.profile.displayName);
  return useMemo(() => {
    void tick;
    const actorId = displayName.trim();
    const listed = projects.filter((project) => (project.lifecycle ?? "active") !== "trashed" && showInPlanList(planVisibilityOf(project.id), role).shown);
    return collectWorkspaceDocuments(listed, (doc, projectId) => {
      const project = listed.find((item) => item.id === projectId);
      return !!project && canReadDocument(doc, readerFor(project, activeId, actor.role, actorId, self));
    }).map((row) => {
      const project = listed.find((item) => item.id === row.projectId)!;
      const reader = readerFor(project, activeId, actor.role, actorId, self);
      return { ...row, reader, contentReadable: canReadDocumentContent(row.doc, reader) };
    });
  }, [projects, role, tick, actor.role, activeId, self, displayName]);
}

function readerFor(project: Project, activeId: string | null, activeRole: DocReader["role"], actorId: string, self: SelfRef): DocReader {
  const here = project.id === activeId;
  return {
    role: here ? activeRole : "owner",
    actorId,
    host: DOCUMENT_HOST,
    status: project.lifecycle ?? "active",
    authorOnPlan: () => true,
    ownExecutionTaskIds: (project.executionTasks ?? []).filter((task) => isOwnExecutionTask(task.assignees, self)).map((task) => task.id),
  };
}

function commitInactive(projectId: string, fn: (project: Project) => Project | null) {
  const current = useCatalogStore.getState().projects[projectId];
  if (!current) return;
  const next = fn(structuredClone(current));
  if (!next) return;
  useCatalogStore.getState().mutateProject(projectId, () => next);
}

/** 열린 플랜은 편집기 이력에 남기고, 다른 플랜은 카탈로그만 고친다. */
export function trashWorkspaceDocument(projectId: string, docId: string) {
  const editor = useProjectStore.getState();
  if (editor.activeId === projectId) {
    editor.trashDocument(docId);
    return;
  }
  const actor = useCatalogStore.getState().profile.displayName;
  commitInactive(projectId, (project) => trashDocument(project, docId, actor));
}

export function setWorkspaceDocumentVisibility(projectId: string, docId: string, visibility: DocumentVisibility) {
  const editor = useProjectStore.getState();
  if (editor.activeId === projectId) {
    editor.setDocumentVisibility(docId, visibility);
    return;
  }
  const actor = useCatalogStore.getState().profile.displayName;
  commitInactive(projectId, (project) => setDocumentVisibility(project, docId, visibility, actor, actor));
}
